import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpContext, HttpParams } from '@angular/common/http';
import { compact } from 'lodash-es';
import { BackendService } from '@shared/services/backend/backend.service';
import { environment } from '@env';
import {
  EmployeePasswordDetails,
  EmployeeActivate,
  RequestPageParams,
  EmployeeList,
  EmployeeListRaw,
  EmployeeRaw,
  Employee,
  FilterValues,
  AddEmployeeParams,
  EmployeeRole,
  SubjectQuickInfo,
  TeamMembersAuditInfo,
} from '@shared/models';
import { constants } from '@shared/constants';
import { filterCreatedAtDateRange, systemTimeZone, toPhone } from '@shared/utils';
import { StorageService } from '../storage/storage.service';
import { IS_EXTENDED_ERRORS } from '@shared/interceptors';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  backendService = inject(BackendService);

  storageService = inject(StorageService);

  teamMemberListFilter = new BehaviorSubject<FilterValues>({});

  public activateEmployeeAccount(passwordDetails: EmployeePasswordDetails): Observable<EmployeeActivate> {
    const realm = 'admin';
    const url = `${environment.accountFlowEndpoint}/auth/realms/${realm}/validate`;
    return this.backendService.post<EmployeeActivate>(url, passwordDetails).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(errorRes)),
    );
  }

  public resetEmployeePassword(passwordDetails: EmployeePasswordDetails): Observable<EmployeeActivate> {
    const realm = 'admin';
    const url = `${environment.accountFlowEndpoint}/auth/realms/${realm}/validate-reset-password`;
    return this.backendService.post<EmployeeActivate>(url, passwordDetails).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(errorRes)),
    );
  }

  public setTeamMemberFilterParams(filters: FilterValues): void {
    this.storageService.setItem(constants.TEAM_MEMBERS_FILTER, filters);
    this.updateTeamMemberFilterParams(filters);
  }

  public getTeamMemberFilterParams(): void {
    const filters = this.storageService.getItem<FilterValues>(constants.TEAM_MEMBERS_FILTER);

    if (filters && Object.keys(filters).length) {
      this.updateTeamMemberFilterParams(filters);
    }
  }

  private updateTeamMemberFilterParams(filters: FilterValues): void {
    this.teamMemberListFilter.next(filters);
  }

  public getBusinessAccountTeamMembers(
    { page, size, searchString, sortParams, activeFilters }: RequestPageParams,
    businessAccountId?: string,
  ): Observable<EmployeeList> {
    const pageSize = size ?? constants.TABLE_ROWS;
    let params = new HttpParams().set('page', `${page ?? 0}`).set('size', `${pageSize}`);

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    if (businessAccountId) {
      params = params.append('businessAccountId', businessAccountId);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        if (key === 'createdAt') {
          const { from, to } = filterCreatedAtDateRange(activeFilters[key]) ?? {};

          if (!from || !to) {
            return;
          }

          params = params.append('createdDateFrom', from);
          params = params.append('createdDateTo', to);
          params = params.append('timeZone', systemTimeZone);
        } else if (key === 'updatedAt') {
          const { from, to } = filterCreatedAtDateRange(activeFilters[key]) ?? {};

          if (!from || !to) {
            return;
          }

          params = params.append('updatedDateFrom', from);
          params = params.append('updatedDateTo', to);
          params = params.append('timeZone', systemTimeZone);
        } else {
          params = params.append(key, activeFilters[key]!);
        }
      });
    }

    return this.backendService.get<EmployeeListRaw>(environment.employeeService, { params }).pipe(
      map((response) => {
        const { content = [], totalElements = 0 } = response || {};

        return {
          items: content?.map((item) => this.mapTeamMemberData(item)),
          totalElements,
        };
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  mapTeamMemberData(teamMember: EmployeeRaw): Employee {
    const phones = teamMember.phoneNumbers || [];
    const primaryPhone = phones.filter((phone) => phone.type !== 'WORK')[0];
    const workPhone = phones.filter((phone) => phone.type === 'WORK')[0];
    const teamMemberRoles: string[] = [];
    teamMember.roles?.forEach((role) => teamMemberRoles.push(role.name));
    const role = teamMemberRoles.join(',');

    return {
      ...teamMember,
      role,
      displayName: compact([teamMember.firstName, teamMember.middleName, teamMember.lastName]).join(' '),
      phoneNumbers: phones,
      status: teamMember.userStatus?.toLowerCase(),
      primaryPhoneNumber: toPhone(teamMember.primaryPhoneNumber),
      shortId: teamMember.id.split('-')[0],
      name: compact([teamMember.firstName, teamMember.middleName, teamMember.lastName]).join(' '),
      initials: `${teamMember.firstName.charAt(0)}${teamMember.lastName.charAt(0)}`,
      primaryPhone,
      workPhone,
      workPhoneNumber: toPhone(workPhone?.number),
      workPhoneNumberExtension: workPhone?.extension,
    };
  }

  getEmployeeDetails(employeeId: string): Observable<Employee> {
    return this.backendService.get<EmployeeRaw>(`${environment.employeeService}/${employeeId}`).pipe(
      map((user) => this.mapTeamMemberData(user)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  addEmployee(employeeDetails: AddEmployeeParams): Observable<EmployeeRaw> {
    return this.backendService.post<EmployeeRaw>(environment.employeeService, employeeDetails).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  updateEmployeeDetails(teamMemberData: AddEmployeeParams, userId: string): Observable<EmployeeRaw> {
    return this.backendService.update<EmployeeRaw>(`${environment.employeeService}/${userId}`, teamMemberData).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  updateEmployeeStatus(userId: string, action: string, businessAccountId: string): Observable<EmployeeRaw> {
    return this.backendService.update<EmployeeRaw>(
      `${environment.employeeService}/${userId}/${action}?businessAccountId=${businessAccountId}`,
    );
  }

  resendWelcomeEmail(employeeId: string, businessAccountId: string): Observable<EmployeeRaw> {
    return this.backendService
      .post<EmployeeRaw>(
        `${environment.employeeService}/${employeeId}/resend-welcome-email?businessAccountId=${businessAccountId}`,
        {},
        { context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
      )
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  deleteEmployee(employeeId: string, businessAccountId: string): Observable<EmployeeRaw> {
    return this.backendService.delete<EmployeeRaw>(`${environment.employeeService}/${employeeId}?businessAccountId=${businessAccountId}`);
  }

  getEmployeeRoles(businessAccountId: string): Observable<EmployeeRole[]> {
    return this.backendService.get<EmployeeRole[]>(`${environment.employeeService}/roles?businessAccountId=${businessAccountId}`);
  }

  getSubjectsQuickInfo(businessAccountId: string, ids: string[]): Observable<TeamMembersAuditInfo> {
    return this.backendService
      .post<SubjectQuickInfo[]>(`/v1/business-accounts/${businessAccountId}/subjects/retrieve`, {
        subjectIds: ids,
      })
      .pipe(
        map((response) => {
          return response.reduce(
            (acc, member) => ({
              ...acc,
              [member.id]: member.name === 'internal' ? 'Internal' : member.name,
            }),
            {},
          );
        }),
      );
  }
}
